import React from 'react';
import { motion } from 'framer-motion';
import { Check, Droplets, Sparkles, ShieldCheck, Car } from 'lucide-react';
type ServiceTier = {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: 'blue' | 'green';
  features: string[];
  priceSm: string;
  priceSuv: string;
  popular?: boolean;
};
const services: ServiceTier[] = [{
  title: 'Basic Wash',
  description: "2 Car Minimum Basic Washes Only",
  icon: <Car className="w-6 h-6" data-id="element-485" data-mp="13wmtbarhb1wx" />,
  color: 'green',
  features: ['Exterior Wash', 'Light Wipe Down', 'Vacuum', 'Clean Rims', 'Dress Tires', 'Clean Windows'],
  priceSm: '$90',
  priceSuv: '$105'
}, {
  title: 'Wash & Wax',
  description: "A Wash and Wax cleans your car, adds a protective layer, helps paint stay shiny, repel water, and dirt.",
  icon: <Droplets className="w-6 h-6" data-id="element-486" data-mp="l5dhguu8k126" />,
  color: 'blue',
  features: ['Exterior Wash', 'Window Cleaning', 'Vacuum', 'Light Wipedown Interior', 'Tire Dressing', 'Hybrid Ceramic Wax'],
  priceSm: '$160',
  priceSuv: '$180'
}, {
  title: 'Full Detail',
  description: 'Our most popular mobile car detailing package — complete interior and exterior restoration for a showroom finish',
  icon: <Sparkles className="w-6 h-6" data-id="element-487" data-mp="3b5al2lcr086h" />,
  color: 'green',
  features: ['Includes Interior Detail', 'Exterior Wash', 'Clay Bar Treatment', 'Clean Rims', 'Dress Tires', 'Hybrid Ceramic Wax', 'Deep Vacuum'],
  priceSm: '$300',
  priceSuv: '$350',
  popular: true
}, {
  title: 'Interior Detail',
  description: 'Professional interior car cleaning service — deep clean, shampoo, steam sanitize every surface inside your vehicle',
  icon: <ShieldCheck className="w-6 h-6" data-id="element-488" data-mp="3g2j5ytpa6qbf" />,
  color: 'blue',
  features: ['Shampoo Seats', 'Deep Clean Interior', 'Vacuum', 'Shampoo Floor Mats', 'Steam & Sanitize'],
  priceSm: '$180',
  priceSuv: '$195'
}];
export function Services() {
  return <section className="w-full bg-black py-16 px-4" id="services" data-id="element-489" data-mp="24mci2sef6owd">
      <div className="max-w-7xl mx-auto" data-id="element-490" data-mp="29mqefd6nsaek">
        <div className="text-center mb-12" data-id="element-491" data-mp="29nry261qq57w">
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-black uppercase mb-4" data-id="element-492" data-mp="1pjuhl9nv90ez">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-493" data-mp="1lihbcygv2v7j">
              Mobile Car Detailing Packages & Pricing
            </span>
          </h2>
          <div className="h-1 w-24 bg-red-600 mx-auto rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-494" data-mp="1bsxqjn9l8ra1" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mt-4" data-id="element-495" data-mp="16u4dfvy7r2ae">
            Transparent pricing for professional mobile car detailing in Ventura
            County. Every service includes premium eco-friendly products, expert
            care, and the convenience of at-your-door service throughout
            Camarillo, Thousand Oaks, and beyond.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8" data-id="element-496" data-mp="2s6xsf3rzuqzx">
          {services.map((service, index) => <ServiceCard key={index} service={service} index={index} data-id="element-497" data-mp="2n7bd8oslv0oh" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  service,
  index
}: {
  service: ServiceTier;
  index: number;
}) {
  const borderColor = service.color === 'blue' ? 'border-blue-500' : 'border-green-500';
  const shadowColor = service.color === 'blue' ? 'shadow-blue-500/20' : 'shadow-green-500/20';
  const titleColor = service.color === 'blue' ? 'text-blue-400' : 'text-green-400';
  const gradient = service.color === 'blue' ? 'from-blue-900/20 to-transparent' : 'from-green-900/20 to-transparent';
  return <motion.div initial={{
    y: 50,
    opacity: 0
  }} whileInView={{
    y: 0,
    opacity: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.2
  }} className={`relative flex flex-col bg-zinc-900/80 backdrop-blur-sm border-2 ${borderColor} rounded-lg overflow-hidden shadow-[0_0_30px_-5px_rgba(0,0,0,0.3)] ${shadowColor} hover:scale-[1.02] transition-transform duration-300`} data-id="element-498" data-mp="2grcn1o11dp8t">
      {/* Header */}
      <div className={`p-6 bg-gradient-to-b ${gradient} border-b border-zinc-800`} data-id="element-499" data-mp="510j8843v02y">
        <div className="flex items-center justify-between mb-2" data-id="element-500" data-mp="3k365pyxqogp">
          <h3 className={`text-2xl sm:text-3xl font-black uppercase tracking-wide sm:tracking-wider whitespace-nowrap ${titleColor}`} data-id="element-501" data-mp="3ur7a2bzs9ukw">
            {service.title}
          </h3>
          <div className={`${titleColor}`} data-id="element-502" data-mp="dkdh13xhzjsp">{service.icon}</div>
        </div>
        <p className="text-gray-400 text-sm mt-1" style={{
        fontSize: "18px",
        fontWeight: "900",
        color: "#FFFFFF"
      }} data-id="element-503" data-mp="8j6dxi6av8sd">{service.description}</p>
      </div>

      {/* Features */}
      <div className="p-6 flex-1" data-id="element-504" data-mp="hk4hjzctteaf">
        <ul className="space-y-4" data-id="element-505" data-mp="w9k6d9ssr9nh">
          {service.features.map((feature, i) => <li key={i} className="flex items-start text-gray-300" data-id="element-506" data-mp="w8s8fsbq8zu1" data-mpi={i}>
              <Check className="w-5 h-5 text-white mr-3 shrink-0" data-id="element-507" data-mp="rbidjx5kjvyt" />
              <span className="font-medium text-lg" data-id="element-508" data-mp="159ys2a9oaqri">{feature}</span>
            </li>)}
        </ul>
      </div>

      {/* Pricing */}
      <div className="p-6 bg-zinc-950 border-t border-zinc-800" data-id="element-509" data-mp="119ob62kcefxv">
        <div className="grid grid-cols-2 gap-4 text-center" data-id="element-510" data-mp="1jx6ykxs1e7x5">
          <div className="flex flex-col" data-id="element-511" data-mp="1jxqeya21go92">
            <span className="text-xs sm:text-sm text-gray-500 uppercase font-bold tracking-wider mb-1 whitespace-nowrap" data-id="element-512" data-mp="1ovs2p8aetbqv">
              SM / MID
            </span>
            <span className={`text-3xl sm:text-4xl font-black ${titleColor}`} data-id="element-513" data-mp="1ayd3xcy9nrm8">
              {service.priceSm}
            </span>
          </div>
          <div className="flex flex-col border-l border-zinc-800" data-id="element-514" data-mp="1167f2c3b9qb9">
            <span className="text-xs sm:text-sm text-gray-500 uppercase font-bold tracking-wider mb-1 whitespace-nowrap" data-id="element-515" data-mp="114mmut4gyjgk">
              SUV / CROSS
            </span>
            <span className={`text-3xl sm:text-4xl font-black ${titleColor}`} data-id="element-516" data-mp="s7ca13c06m9j">
              {service.priceSuv}
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
}